import * as React from "react";

import { AccessOverviewEditChartColorsElementEnum } from "@app/access-reports/admin";
import { AccessReportsWizardElement, AccessReportsWizardElementTypeEnum, ByProductDefault } from "@app/access-reports/shared";
import { TooltipWrapper } from "@app/shared";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { cloneDeep, isEqual } from "lodash";
import { ColorResult, SketchPicker } from "react-color";
import { FlyoutFooter } from "@app/shared/Flyouts";

interface AccessOverviewEditChartColorsProps {
  byProductDefault: ByProductDefault;
  disableButtons: boolean;
  elements: AccessReportsWizardElement[];
  hideSidePanel: () => void;
  onSaveAccessOverviewChartColorsClick: (byProductDefault: ByProductDefault) => void;
}

interface AccessOverviewEditChartColorsState {
  clonedByProductDefault: ByProductDefault;
  displayColorPicker: AccessOverviewEditChartColorsElementEnum;
}

class AccessOverviewEditChartColors extends React.Component<AccessOverviewEditChartColorsProps, AccessOverviewEditChartColorsState> {
  constructor(props: AccessOverviewEditChartColorsProps) {
    super(props);

    this.state = {
      clonedByProductDefault: null,
      displayColorPicker: null,
    };
  }

  public componentDidMount() {
    this.extractChartColorDefaults();
  }

  public componentDidUpdate(prevProps: AccessOverviewEditChartColorsProps) {
    if (!isEqual(prevProps.byProductDefault, this.props.byProductDefault)) {
      this.extractChartColorDefaults();
    }
  }

  public render() {
    const disabledSavedButtonTooltipText = this.buildDisabledSaveButtonTooltipText();

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <label className="label-m, mb-3">
              Access Filter Pie Chart Options
            </label>
          </div>
        </div>

        <div className="row align-items-center mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.WithColor, true)}
        </div>

        <div className="row align-items-center mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.WithoutColor, true)}
        </div>

        <div className="row align-items-center mb-5">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ExcludedColor, true)}
        </div>

        <div className="row">
          <div className="col-12">
            <label className="label-m mb-3">
              Distance Chart Options
            </label>
          </div>
          <div className="col-12">
            <label className="label-s mb-0">
              Providers
              <span className="text-danger">*</span>
            </label>
          </div>
        </div>

        <div className="row mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderOne)}
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderOneColor)}
        </div>

        <div className="row mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderTwo)}
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderTwoColor)}
        </div>

        <div className="row mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderThree)}
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderThreeColor)}
        </div>

        <div className="row mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFour)}
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFourColor)}
        </div>

        <div className="row mb-5">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFive)}
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ProviderFiveColor)}
        </div>

        <div className="row align-items-center mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.GraphMiles, true, "Please input a value between 1 and 99")}
        </div>

        <div className="row align-items-center mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ShowMinimums)}
        </div>

        <div className="row align-items-center mb-3">
          {this.buildAccessOverviewElement(AccessOverviewEditChartColorsElementEnum.ShowMaximums)}
        </div>

        <FlyoutFooter
          submitButton={
            <TooltipWrapper
              text={disabledSavedButtonTooltipText}
              position="top"
            >
              <QESButton
                disabled={this.props.disableButtons || !!disabledSavedButtonTooltipText}
                onClick={() => this.props.onSaveAccessOverviewChartColorsClick(this.state.clonedByProductDefault)}
                qesTestId="access-reports-admin__access-overview-chart-colors--save"
                qesType="primary"
              >
                Save
              </QESButton>
            </TooltipWrapper>
          }
        />
      </React.Fragment>
    );
  }

  private buildAccessOverviewElement = (elementId: AccessOverviewEditChartColorsElementEnum, required = false, errorText?: string): React.ReactNode => {
    const element: AccessReportsWizardElement = this.props.elements.find(x => x.elementId === elementId);
    const value: unknown = this.getValue(elementId);

    if (!!element) {
      switch (element.elementType) {
        case AccessReportsWizardElementTypeEnum.Checkbox:
          return (
            <div className="col-12">
              <QESCheckbox
                checked={value as boolean}
                disabled={this.props.disableButtons}
                label={element.title}
                testId={`access-reports-admin__access-overview-chart-colors--${element.elementId}-checkbox`}
                onChange={() => this.onCheckboxToggle(element.elementId as AccessOverviewEditChartColorsElementEnum)}
              />
            </div>
          );
        case AccessReportsWizardElementTypeEnum.ColorPicker:
          const presetColors: string[] = element.options.map(x => x.color as string);

          return (
            <React.Fragment>
              {!!element.title && (
                <div className="col-md-6">
                  <label className="label-s mb-0">
                    {element.title}
                    {required && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                </div>
              )}

              <div className="col-md-6 d-flex align-items-center">
                <div className="color-picker-swatch" onClick={() => this.onColorPickerClick(element.elementId as AccessOverviewEditChartColorsElementEnum)}>
                  <div className="color-picker-color" style={{ backgroundColor: value as string || "#000" }} />
                </div>

                {(this.state.displayColorPicker === element.elementId) &&
                  <div className="color-picker-popover">
                    <div
                      className="color-picker-cover"
                      onClick={() => this.onColorPickerClose()}
                    />

                    <SketchPicker
                      color={value as string || "#000"}
                      disableAlpha={true}
                      presetColors={presetColors}
                      onChange={this.onColorPickerChange}
                    />
                  </div>
                }
              </div>
            </React.Fragment>
          );
        case AccessReportsWizardElementTypeEnum.Dropdown:
          return (
            <React.Fragment>
              {!!element.title && (
                <div className="col-md-6">
                  <label className="label-s mb-0">
                    {element.title}
                    {required && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                </div>
              )}

              <div className="col-md-6">
                <QESSelect
                  allowEmpty={true}
                  disabled={this.props.disableButtons}
                  testId={`access-reports-admin__access-overview-chart-colors--${element.elementId}-dropdown`}
                  label=""
                  onChange={(e) => this.onSelectChange(element.elementId as AccessOverviewEditChartColorsElementEnum, e)}
                  value={value.toString() || ""}
                >
                  {!!element.options && element.options.map((option, optionIndex) => (
                    <option
                      key={optionIndex}
                      value={option.id}
                    >
                      {option.displayName ? option.displayName : option.name}
                    </option>
                  ))}
                </QESSelect>
              </div>
            </React.Fragment>
          );
        case AccessReportsWizardElementTypeEnum.Input:
          return (
            <React.Fragment>
              {!!element.title && (
                <div className="col-md-6">
                  <label className="label-s mb-0">
                    {element.title}
                    {required && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                </div>
              )}

              <div className="col-md-6">
                <QESTextInput
                  testId={`access-reports-admin__access-overview-chart-colors--${element.elementId}-input`}
                  disabled={this.props.disableButtons}
                  errorText={errorText}
                  isValid={this.getInputValidity(element.elementId as AccessOverviewEditChartColorsElementEnum, value?.toString())}
                  label=""
                  onChange={(e) => this.onInputChange(element.elementId as AccessOverviewEditChartColorsElementEnum, e)}
                  unrequiredValidation={required}
                  value={value.toString() || ""}
                />
              </div>
            </React.Fragment>
          );
        default:
          console.error("Invalid Access Reports Overview Element Type");
          break;
      }
    }

    return (
      <div className="form-group mb-3">
        <i className="text-danger fa fa-fw fa-exclamation-circle" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  private buildDisabledSaveButtonTooltipText = (): string => {
    let tooltipText = "";

    if (this.props.disableButtons) {
      return "In order to save access overview chart colors, please check-out the template";
    }

    // only assess whether the hidden elements, specific to editing chart colors, have default values in place
    const elements: AccessReportsWizardElement[] = this.props.elements.filter(x => x.hidden);

    for (const element of elements) {
      if (element.defaultValue === null || element.defaultValue === undefined || element.defaultValue === "") {
        if (!tooltipText) {
          tooltipText = tooltipText + "Please provide a value for the following elements:";
          tooltipText = tooltipText + " " + this.splitKey(element.elementId);
        } else {
          tooltipText = tooltipText + ", " + this.splitKey(element.elementId);
        }
      }
    }

    return tooltipText;
  }

  private extractChartColorDefaults = (): void => {
    if (!!this.props.byProductDefault) {
      const clonedByProductDefault: ByProductDefault = cloneDeep(this.props.byProductDefault);

      this.setState({
        clonedByProductDefault,
      });
    }
  }

  private getInputValidity = (elementId: AccessOverviewEditChartColorsElementEnum, value: string): boolean => {
    if (elementId === AccessOverviewEditChartColorsElementEnum.GraphMiles) {
      if (!value.match(/^[0-9]*$/) || parseInt(value) > 99 || parseInt(value) < 1) {
        return false;
      }
    }

    return true;
  }

  private getValue = (elementId: AccessOverviewEditChartColorsElementEnum): unknown => {
    if (!!this.state.clonedByProductDefault) {
      return this.state.clonedByProductDefault[elementId];
    }

    return "";
  }

  private onCheckboxToggle = (elementId: AccessOverviewEditChartColorsElementEnum): void => {
    const clonedByProductDefault: ByProductDefault = this.state.clonedByProductDefault;
    clonedByProductDefault[elementId] = !clonedByProductDefault[elementId] as boolean;

    this.setState({
      clonedByProductDefault,
    });
  }

  private onInputChange = (elementId: AccessOverviewEditChartColorsElementEnum, event: React.ChangeEvent<HTMLInputElement>): void => {
    const clonedByProductDefault: ByProductDefault = this.state.clonedByProductDefault;
    const value: string = event.target.value;

    if (elementId === AccessOverviewEditChartColorsElementEnum.GraphMiles) {
      // parsing the value for comparison and to remove leading zeros
      const parsedValue = parseInt(value);

      if (isNaN(parsedValue) || parsedValue === 0) {
        // if the graph miles input is not a number or 0, display the min value, 1
        clonedByProductDefault[elementId] = 1;
      } else {
        if (value.match(/^[0-9]*$/) && parsedValue <= 99 && parsedValue >= 1) {
          // only whole numbers between 1 and 99 are valid values
          clonedByProductDefault[elementId] = parsedValue;
        }
      }
    } else {
      clonedByProductDefault[elementId] = value;
    }

    this.setState({
      clonedByProductDefault,
    });
  }

  private onSelectChange = (elementId: AccessOverviewEditChartColorsElementEnum, event: React.ChangeEvent<HTMLSelectElement>): void => {
    const clonedByProductDefault: ByProductDefault = this.state.clonedByProductDefault;
    clonedByProductDefault[elementId] = event.target.value;

    this.setState({
      clonedByProductDefault,
    });
  }

  private splitKey = (key: string): string => {
    const splitKey: string = key.replace(/([A-Z])/g, " $1");
    const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

    return formattedKey;
  }

  private onColorPickerClick = (accessOverviewElementId: AccessOverviewEditChartColorsElementEnum): void => {
    if (accessOverviewElementId === this.state.displayColorPicker) {
      this.setState({
        displayColorPicker: null,
      });
    } else {
      this.setState({
        displayColorPicker: accessOverviewElementId,
      });
    }
  }

  private onColorPickerChange = (color: ColorResult): void => {
    const clonedByProductDefault: ByProductDefault = this.state.clonedByProductDefault;
    clonedByProductDefault[this.state.displayColorPicker] = color.hex;

    this.setState({
      clonedByProductDefault,
    });
  }

  private onColorPickerClose = (): void => {
    this.setState({
      displayColorPicker: null,
    });
  }
}

export { AccessOverviewEditChartColors };
